import type { Vec2 } from '../core/math'
import { len, norm } from '../core/math'

/**
 * Game logic reads INTENT, never hardware. A player entity is "an entity
 * whose intent comes from an InputSource" — a second player is another
 * entity with another source (or a network stream). Co-op-ready by design.
 */
export interface Intent {
  /** desired movement, magnitude 0..1 */
  move: Vec2
  /** unit aim direction (world space) */
  aim: Vec2
  /** hold-to-autofire */
  fire: boolean
  /** edge-triggered: true only on the tick the button was pressed */
  magic: boolean
  /** edge-triggered */
  interact: boolean
  /** edge-triggered OVERRIDE (slow-motion) activation request */
  override: boolean
  /** edge-triggered pause request (routed to game shell, not simulation) */
  pause: boolean
}

export const emptyIntent = (): Intent => ({
  move: { x: 0, y: 0 },
  aim: { x: 1, y: 0 },
  fire: false,
  magic: false,
  interact: false,
  override: false,
  pause: false
})

export interface InputSource {
  /** Poll once per simulation tick. World-space player position lets sources resolve aim. */
  poll(px: number, py: number): Intent
  /** Classic toggle: re-link facing to movement (shoot-where-you-walk). */
  setClassicMode(on: boolean): void
  /** Drop edge-triggered presses queued while the sim wasn't polling (pause/menus). */
  clearQueued(): void
  destroy(): void
}

/**
 * Classic-aim behavior shared by all sources: aim follows the last nonzero
 * movement direction. A source-level switch, not a game-logic change.
 */
export class ClassicAimHelper {
  private lastDir: Vec2 = { x: 1, y: 0 }

  apply(intent: Intent): Intent {
    if (len(intent.move) > 0.01) this.lastDir = norm(intent.move)
    intent.aim = { ...this.lastDir }
    return intent
  }
}
